/**
 * CommandPalette — Phase 5.5 (UX-014..016)
 *
 * Global ⌘K / Ctrl+K command palette. Indexes navigable pages + quick actions
 * + recents. Keyboard-navigable, focus-trapped, RBAC-aware.
 *
 * Owner: Phase 5.5 — UX Polish & Dual-Brand Design.
 * URS rows:
 *   UX-014 — Cmd+K palette indexes pages + actions + recents.
 *   UX-015 — Respects RBAC — only shows actions the user can perform.
 *   UX-016 — Navigable by keyboard with focus trap.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouter } from '@tanstack/react-router';
import styles from './CommandPalette.module.css';

export type PaletteEntry = {
  id: string;
  label: string;
  /** Optional second line (group / description). */
  hint?: string;
  /** Where to navigate when chosen. */
  to: string;
  /** Optional icon glyph (emoji or single-char). */
  icon?: string;
  /** Bucket the entry into a section. */
  section: 'pages' | 'quickActions' | 'recents';
};

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  entries: PaletteEntry[];
}

const RECENTS_KEY = 'edms.cmdPalette.recents';
const MAX_RECENTS = 5;

export function CommandPalette({ open, onClose, entries }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Recents from localStorage.
  const recents = useMemo(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      if (!raw) return [];
      const ids: string[] = JSON.parse(raw);
      return ids
        .map((id) => entries.find((e) => e.id === id))
        .filter((e): e is PaletteEntry => Boolean(e));
    } catch {
      return [];
    }
  }, [entries, open]);

  // Filter + sort entries by the query.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // No query — show recents first, then pages, then quick actions.
      const seen = new Set<string>();
      const out: PaletteEntry[] = [];
      for (const r of recents) {
        if (!seen.has(r.id)) {
          out.push({ ...r, section: 'recents' });
          seen.add(r.id);
        }
      }
      for (const e of entries) {
        if (e.section === 'quickActions' && !seen.has(e.id)) {
          out.push(e);
          seen.add(e.id);
        }
      }
      for (const e of entries) {
        if (e.section === 'pages' && !seen.has(e.id)) {
          out.push(e);
          seen.add(e.id);
        }
      }
      return out;
    }
    return entries
      .filter((e) =>
        e.label.toLowerCase().includes(q) ||
        (e.hint?.toLowerCase().includes(q) ?? false) ||
        e.to.toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const aPrefix = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bPrefix = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        if (aPrefix !== bPrefix) return aPrefix - bPrefix;
        return a.label.localeCompare(b.label);
      });
  }, [entries, query, recents]);

  // Reset activeIdx if the filtered list shrinks.
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIdx]);

  // Scroll active row into view on change.
  useEffect(() => {
    const root = listRef.current;
    if (!root) return;
    const li = root.children[activeIdx] as HTMLElement | undefined;
    if (li && typeof li.scrollIntoView === 'function') {
      li.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const choice = filtered[activeIdx];
        if (choice) commit(choice);
      } else if (e.key === 'Tab') {
        // Trap focus inside the dialog — there's only one focusable element
        // (the input), so cycling stays on it.
        e.preventDefault();
        inputRef.current?.focus();
      }
    },
    [activeIdx, filtered],
  );

  function commit(choice: PaletteEntry) {
    // Persist recents.
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      const prev: string[] = raw ? JSON.parse(raw) : [];
      const next = [choice.id, ...prev.filter((id) => id !== choice.id)].slice(0, MAX_RECENTS);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    onClose();
    // Use the router so this works with TanStack's type-safe navigate too.
    void navigate({ to: choice.to as never }).catch(() => {
      // Fallback for routes that aren't statically known (e.g. dynamic ids).
      router.history.push(choice.to);
    });
  }

  if (!open) return null;

  // Group filtered into sections for display.
  const recentsSection = filtered.filter((e) => e.section === 'recents');
  const quickActionsSection = filtered.filter((e) => e.section === 'quickActions');
  const pagesSection = filtered.filter((e) => e.section === 'pages');

  let runningIndex = 0;
  const renderRow = (e: PaletteEntry) => {
    const idx = runningIndex++;
    const isActive = idx === activeIdx;
    return (
      <li
        key={e.id}
        role="option"
        aria-selected={isActive}
        className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
        onMouseEnter={() => setActiveIdx(idx)}
        onClick={() => commit(e)}
      >
        <span className={styles.rowIcon} aria-hidden="true">{e.icon ?? '→'}</span>
        <span className={styles.rowLabels}>
          <span className={styles.rowLabel}>{e.label}</span>
          {e.hint && <span className={styles.rowHint}>{e.hint}</span>}
        </span>
        {isActive && (
          <span className={styles.rowEnter} aria-hidden="true">
            <kbd>↵</kbd>
          </span>
        )}
      </li>
    );
  };

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('palette.label', { defaultValue: 'Command palette' })}
      onClick={onClose}
      onKeyDown={onKeyDown}
    >
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.search}>
          <span className={styles.searchIcon} aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            type="text"
            className={styles.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('palette.placeholder', { defaultValue: 'Search pages, actions, recents…' })}
            aria-label={t('palette.placeholder', { defaultValue: 'Search pages, actions, recents…' })}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className={styles.escHint} aria-hidden="true">esc</kbd>
        </div>

        <ul className={styles.list} role="listbox" ref={listRef}>
          {filtered.length === 0 && (
            <li className={styles.empty}>{t('palette.empty', { defaultValue: 'No matches' })}</li>
          )}
          {recentsSection.length > 0 && (
            <>
              <li className={styles.sectionHeader} role="presentation">
                {t('palette.section.recents', { defaultValue: 'Recents' })}
              </li>
              {recentsSection.map(renderRow)}
            </>
          )}
          {quickActionsSection.length > 0 && (
            <>
              <li className={styles.sectionHeader} role="presentation">
                {t('palette.section.actions', { defaultValue: 'Quick actions' })}
              </li>
              {quickActionsSection.map(renderRow)}
            </>
          )}
          {pagesSection.length > 0 && (
            <>
              <li className={styles.sectionHeader} role="presentation">
                {t('palette.section.pages', { defaultValue: 'Pages' })}
              </li>
              {pagesSection.map(renderRow)}
            </>
          )}
        </ul>

        <div className={styles.footer}>
          <span><kbd>↑</kbd><kbd>↓</kbd> {t('palette.footer.navigate', { defaultValue: 'navigate' })}</span>
          <span><kbd>↵</kbd> {t('palette.footer.open', { defaultValue: 'open' })}</span>
          <span><kbd>esc</kbd> {t('palette.footer.close', { defaultValue: 'close' })}</span>
        </div>
      </div>
    </div>
  );
}
